import {
  Control,
  Controller,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
} from 'react-hook-form';

import { HubSpotContact } from '../../../api/billing-types';
import { isAbleToSeeUIElement } from '../../../utils/roles/client-roles';
import { BillingRoles } from '../../../utils/roles/client-roles';
import { countries, states } from '../../../shared/hubspot-address-utils';

import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface ShippingAddressFormProps {
  control: Control<Partial<HubSpotContact>>;
  errors: FieldErrors<Partial<HubSpotContact>>;
  watch: UseFormWatch<Partial<HubSpotContact>>;
  setValue: UseFormSetValue<Partial<HubSpotContact>>;
  billingRoles: string[];
  formType: 'create' | 'edit';
}

export default function ShippingAddressForm({
  control,
  errors,
  watch,
  setValue,
  billingRoles,
  formType,
}: ShippingAddressFormProps) {
  const selectedShippingCountry = watch('properties.country__shipping_') || '';

  const canEditContacts =
    isAbleToSeeUIElement(billingRoles, BillingRoles.canEditContacts.value) ||
    formType === 'create';

  return (
    <Tooltip
      arrow
      title={
        canEditContacts
          ? ''
          : `You need the permission "${BillingRoles.canEditContacts.name}" to change the shipping address.`
      }
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }} sx={{ mb: -1 }}>
          <Typography variant="body1">Shipping Address</Typography>
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.street_address_1__shipping_'}
            control={control}
            rules={{
              required: `Please provide a street address.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address Line 1"
                fullWidth
                required
                error={!!errors.properties?.street_address_1__shipping_}
                helperText={
                  errors.properties?.street_address_1__shipping_
                    ? errors.properties?.street_address_1__shipping_.message
                    : ' '
                }
                autoComplete="address-line1"
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.street_address_2__shipping_'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Address Line 2"
                fullWidth
                autoComplete="address-line2"
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.city__shipping_'}
            control={control}
            rules={{
              required: `Please provide a city.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="City"
                required
                fullWidth
                error={!!errors.properties?.city__shipping_}
                helperText={
                  errors.properties?.city__shipping_
                    ? errors.properties?.city__shipping_.message
                    : ' '
                }
                autoComplete="address-level2"
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name="properties.state__shipping_"
            control={control}
            rules={{
              required:
                selectedShippingCountry === 'US'
                  ? 'State is required'
                  : 'Province is required',
              validate: (value) => {
                if (!value) return true;
                if (selectedShippingCountry === 'US') {
                  return (
                    states.some((state) => state.abbreviation === value) ||
                    'Please select a valid state'
                  );
                }
                return true;
              },
            }}
            render={({ field }) =>
              selectedShippingCountry === 'US' ? (
                <TextField
                  {...field}
                  label="State"
                  fullWidth
                  autoComplete="state"
                  select
                  slotProps={{
                    select: {
                      MenuProps: {
                        PaperProps: {
                          style: {
                            maxHeight: 450,
                          },
                        },
                      },
                    },
                  }}
                  required
                  error={!!errors.properties?.state__shipping_}
                  helperText={
                    errors.properties?.state__shipping_?.message || ' '
                  }
                >
                  {states.map((state) => (
                    <MenuItem
                      key={state.abbreviation}
                      value={state.abbreviation}
                    >
                      {state.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField
                  {...field}
                  label="State / Province"
                  fullWidth
                  autoComplete="province"
                  required
                  error={!!errors.properties?.state__shipping_}
                  helperText={
                    errors.properties?.state__shipping_?.message || ' '
                  }
                />
              )
            }
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.postal_code__shipping_'}
            control={control}
            rules={{
              required: `Please provide a zip / postal code.`,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                required
                label="Zip / Postal Code"
                fullWidth
                autoComplete="postal-code"
                error={!!errors.properties?.postal_code__shipping_}
                helperText={
                  errors.properties?.postal_code__shipping_
                    ? errors.properties?.postal_code__shipping_.message
                    : ' '
                }
                disabled={!canEditContacts}
              />
            )}
          />
        </Grid>

        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Controller
            name={'properties.country__shipping_'}
            control={control}
            rules={{
              required: `Please provide a country.`,
              validate: (value) => {
                if (!value) return true;
                return (
                  countries.some((country) => country.abbreviation === value) ||
                  'Please select a valid country'
                );
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                onChange={(e) => {
                  field.onChange(e);
                  if (e.target.value === 'US') {
                    setValue('properties.state__shipping_', '');
                  }
                }}
                required
                label="Country/Region"
                fullWidth
                autoComplete="country"
                error={!!errors.properties?.country__shipping_}
                helperText={
                  errors.properties?.country__shipping_
                    ? errors.properties?.country__shipping_.message
                    : ' '
                }
                select
                slotProps={{
                  select: {
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 450,
                        },
                      },
                    },
                  },
                }}
                disabled={!canEditContacts}
              >
                {countries.map((country) => (
                  <MenuItem
                    key={country.abbreviation}
                    value={country.abbreviation}
                  >
                    {country.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>
    </Tooltip>
  );
}
